"use client";

import type { CatalogRails, RailOption } from "@/lib/host-panel/catalog-rails";
import { CatalogChip } from "./catalog-filter-chip";
import { CatalogChipTrack, CatalogFilterRow, centreChipInTrack } from "./catalog-filter-row";

/**
 * The department and category lines of the filter block (handoff v2 §6.2, filters spec §4).
 *
 * Both are rows of the same chip, each a single-choice `radiogroup`. The type of piece is no
 * longer a third track: since v2 it is an attribute ("Tipo"), beside collection and material.
 * The label counts the options the line offers, its "all" included, as the prototype does.
 */

export type TaxonomyRailProps = {
  rails: CatalogRails;
  onSelectMacro: (value: string) => void;
  onSelectCategory: (value: string) => void;
  /** N3: the type inside the chosen category (handoff §2 item 6). */
  onSelectType?: (value: string) => void;
};

export function TaxonomyRail({ rails, onSelectMacro, onSelectCategory, onSelectType }: TaxonomyRailProps) {
  return (
    <>
      {/* Each line draws only when it has something to separate (filters spec §4). */}
      {rails.hasMacroRail ? (
        <TaxonomyLine
          label="Departamentos"
          labelId="catalogo-departamentos"
          options={rails.macros}
          onSelect={onSelectMacro}
        />
      ) : null}

      {rails.hasCategoryRail ? (
        <TaxonomyLine
          // Named by the department: "Coleções" lists collections (`CatalogRails.categoryLabel`).
          label={rails.categoryLabel}
          labelId="catalogo-categorias"
          options={rails.categories}
          onSelect={onSelectCategory}
        />
      ) : null}
      {/* N3 exists only with two or more types in the scope, and never as a dropdown (§4). */}
      {rails.hasTypeRail && onSelectType !== undefined ? (
        <TaxonomyLine
          label="Tipos de peça"
          labelId="catalogo-tipos"
          options={rails.types}
          onSelect={onSelectType}
        />
      ) : null}
    </>
  );
}

function TaxonomyLine({
  label,
  labelId,
  options,
  onSelect,
}: {
  label: string;
  labelId: string;
  options: readonly RailOption[];
  onSelect: (value: string) => void;
}) {
  return (
    <CatalogFilterRow label={label} count={`(${options.length})`} labelId={labelId}>
      <CatalogChipTrack role="radiogroup" aria-labelledby={labelId}>
        {options.map((option) => (
          <CatalogChip
            key={option.value}
            role="radio"
            aria-checked={option.isActive}
            label={option.label}
            // "Coleções" carries no count, and still keeps the 8px gap an empty count leaves.
            count={option.count}
            isActive={option.isActive}
            onClick={(event) => {
              centreChipInTrack(event.currentTarget);
              onSelect(option.value);
            }}
          />
        ))}
      </CatalogChipTrack>
    </CatalogFilterRow>
  );
}
